// Libs
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

// Components
import Input from '../../../Field/Input';
import FooterButtons from '../../../Modal/FooterButtons';

// Actions
import { CleanSharedKitActionError } from '../../../../actions/sharedKits';

// Hooks
import useSharedKitsSettings from '../../../../hooks/useSharedKitsSettings';
import useUser from '../../../../hooks/useUser';

// Constants
import {
  APPROVAL_MODE,
  RESTRICTION_PERIOD,
  SHARED_KIT_MODAL_TITLE,
  approveAllOrdersHint,
  approveAllOrdersLabel,
  attendeeCountRequiredHint,
  attendeeCountRequiredLabel,
  kitNicknameLabel,
  maxQuantityLabel,
  optionalSectionLabel,
  restrictionPeriodLabel,
  restrictionPeriods,
  subgroupsLabel,
} from '../../../../constants/sharedKits';

// Styles
import { useStyles } from './SharedKitModalStyles';

const RequiredLabel = ({ text, className }) => {
  const { classes, cx } = useStyles();

  return (
    <Typography className={cx(classes.sectionLabel, className)}>
      <span className={classes.required}>*</span>
      {text}
    </Typography>
  );
};

const SharedKitModal = ({ kit, onClickSave, onClickCancel }) => {
  const dispatch = useDispatch();
  const { classes, cx } = useStyles();
  const { subgroups } = useSharedKitsSettings();
  const { accessGroup } = useUser();
  const actionError = useSelector((state) => state.sharedKits?.actionError);

  const [name, setName] = useState(kit?.name || '');
  const [subgroupIds, setSubgroupIds] = useState(
    () => kit?.subgroups?.map(({ id }) => id) || [],
  );
  const [restrictionQuantity, setRestrictionQuantity] = useState(
    kit?.restriction_quantity ? String(kit.restriction_quantity) : '',
  );
  const [restrictionPeriod, setRestrictionPeriod] = useState(
    kit?.restriction_period &&
      kit.restriction_period !== RESTRICTION_PERIOD.none
      ? kit.restriction_period
      : '',
  );
  const [approveAllOrders, setApproveAllOrders] = useState(
    kit?.approval_mode === APPROVAL_MODE.always,
  );
  const [attendeeCountRequired, setAttendeeCountRequired] = useState(
    !!kit?.attendee_count_required,
  );

  useEffect(() => {
    return () => dispatch(CleanSharedKitActionError());
  }, [dispatch]);

  const toggleSubgroup = (subgroupId) =>
    setSubgroupIds((current) =>
      current.includes(subgroupId)
        ? current.filter((id) => id !== subgroupId)
        : [...current, subgroupId],
    );

  const isValid = useMemo(
    () =>
      !!name.trim() &&
      subgroupIds.length > 0 &&
      Number(restrictionQuantity) > 0 &&
      !!restrictionPeriod,
    [name, subgroupIds, restrictionQuantity, restrictionPeriod],
  );

  const onSave = () =>
    onClickSave({
      name: name.trim(),
      subgroup_ids: subgroupIds,
      restriction_period: restrictionPeriod,
      restriction_quantity: Number(restrictionQuantity),
      approval_mode: approveAllOrders
        ? APPROVAL_MODE.always
        : APPROVAL_MODE.overLimit,
      attendee_count_required: attendeeCountRequired,
    });

  return (
    <>
      <Typography variant="h3" mb={3}>
        {SHARED_KIT_MODAL_TITLE}
      </Typography>

      <RequiredLabel text={kitNicknameLabel} />
      <Input
        id="shared-kit-name-input"
        onChange={({ value }) => setName(value)}
        value={name}
        maxLength={256}
        error={actionError}
        data-testid="shared-kit-name-input"
      />

      <Box mt={3}>
        <RequiredLabel text={subgroupsLabel(accessGroup)} />
        <div
          className={cx(classes.controls, classes.subgroups, classes.section)}>
          {subgroups.map((subgroup) => (
            <FormControlLabel
              key={subgroup.id}
              control={
                <Checkbox
                  checked={subgroupIds.includes(subgroup.id)}
                  onChange={() => toggleSubgroup(subgroup.id)}
                  data-testid={`shared-kit-subgroup-${subgroup.id}`}
                />
              }
              label={subgroup.name}
            />
          ))}
        </div>
      </Box>

      <div
        className={cx(classes.controls, classes.quantityRow, classes.section)}>
        <Input
          id="shared-kit-restriction-quantity-input"
          className={classes.quantityInput}
          onChange={({ value }) =>
            setRestrictionQuantity(value.replace(/\D/g, ''))
          }
          value={restrictionQuantity}
          maxLength={6}
          data-testid="shared-kit-restriction-quantity-input"
        />
        <RequiredLabel text={maxQuantityLabel} className={classes.noMargin} />
      </div>

      <RequiredLabel text={restrictionPeriodLabel} />
      <RadioGroup
        className={cx(classes.controls, classes.radioGroup, classes.section)}
        value={restrictionPeriod}
        onChange={(event) => setRestrictionPeriod(event.target.value)}>
        {restrictionPeriods.map(({ value, label }) => (
          <FormControlLabel
            key={value}
            value={value}
            control={<Radio data-testid={`shared-kit-period-${value}`} />}
            label={label}
          />
        ))}
      </RadioGroup>

      <Box mt={2}>
        <Typography className={classes.sectionLabel}>
          {optionalSectionLabel}
        </Typography>
        <div className={cx(classes.controls, classes.option)}>
          <FormControlLabel
            className={classes.optionLabel}
            control={
              <Checkbox
                checked={approveAllOrders}
                onChange={() => setApproveAllOrders((current) => !current)}
                data-testid="shared-kit-approve-all-orders"
              />
            }
            label={approveAllOrdersLabel}
          />
          <Typography className={classes.hint}>
            {approveAllOrdersHint}
          </Typography>
        </div>
        <div className={cx(classes.controls, classes.option)}>
          <FormControlLabel
            className={classes.optionLabel}
            control={
              <Checkbox
                checked={attendeeCountRequired}
                onChange={() => setAttendeeCountRequired((current) => !current)}
                data-testid="shared-kit-attendee-count-required"
              />
            }
            label={attendeeCountRequiredLabel}
          />
          <Typography className={classes.hint}>
            {attendeeCountRequiredHint}
          </Typography>
        </div>
      </Box>

      <FooterButtons
        onReject={onClickCancel}
        acceptTitle="Save"
        rejectTitle="Cancel"
        onAccept={onSave}
        disabledAcceptBtn={!isValid}
      />
    </>
  );
};

export default SharedKitModal;
